import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, UserPlus, Key, Shield } from 'lucide-react';
import { usersApi, locationsApi } from '@/api/client';
import { toast } from '@/utils/toast';

const userSchema = z.object({
    username: z.string().min(3, 'Username must be at least 3 characters'),
    password: z.string().min(8, 'Password must be at least 8 characters').optional(),
    fullName: z.string().min(1, 'Full name is required'),
    email: z.string().email('Invalid email').optional().or(z.literal('')),
    role: z.enum(['admin', 'staff']),
    locationIds: z.array(z.string()).optional(),
});

type UserFormData = z.infer<typeof userSchema>;

export function UsersPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [resetPasswordUserId, setResetPasswordUserId] = useState<string>('');
    const queryClient = useQueryClient();

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: usersApi.list,
    });

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const createMutation = useMutation({
        mutationFn: usersApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toast.success('User created successfully');
            setIsModalOpen(false);
            setEditingUser(null);
            reset({
                username: '',
                password: '',
                fullName: '',
                email: '',
                role: 'staff',
                locationIds: [],
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create user');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<UserFormData> }) =>
            usersApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toast.success('User updated successfully');
            setIsModalOpen(false);
            setEditingUser(null);
            reset({
                username: '',
                password: '',
                fullName: '',
                email: '',
                role: 'staff',
                locationIds: [],
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update user');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: usersApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toast.success('User deactivated successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to deactivate user');
        },
    });

    const resetPasswordMutation = useMutation({
        mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
            usersApi.resetPassword(id, newPassword),
        onSuccess: () => {
            toast.success('Password reset successfully');
            setIsPasswordModalOpen(false);
            setResetPasswordUserId('');
            resetPasswordForm.reset();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to reset password');
        },
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
    } = useForm<UserFormData>({
        resolver: zodResolver(userSchema),
    });

    const resetPasswordForm = useForm<{ newPassword: string }>({
        defaultValues: { newPassword: '' },
    });

    const onSubmit = (data: UserFormData) => {
        if (editingUser) {
            const { password, ...updateData } = data;
            updateMutation.mutate({ id: editingUser.id, data: updateData });
        } else {
            createMutation.mutate(data as any);
        }
    };

    const handleEdit = (user: any) => {
        setEditingUser(user);
        reset({
            ...user,
            password: undefined,
            email: user.email || '',
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingUser(null);
        reset({
            username: '',
            password: '',
            fullName: '',
            email: '',
            role: 'staff',
            locationIds: [],
        });
    };

    const handleResetPassword = (userId: string) => {
        setResetPasswordUserId(userId);
        setIsPasswordModalOpen(true);
    };

    const onSubmitPasswordReset = (data: { newPassword: string }) => {
        resetPasswordMutation.mutate({
            id: resetPasswordUserId,
            newPassword: data.newPassword,
        });
    };

    const roleColors = {
        admin: 'bg-purple-100 text-purple-700',
        staff: 'bg-blue-100 text-blue-700',
        super_admin: 'bg-red-100 text-red-700',
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Users</h1>
                    <p className="text-slate-600">Manage user accounts and permissions</p>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="btn btn-primary btn-md">
                    <UserPlus className="w-4 h-4" />
                    Add User
                </button>
            </div>

            {/* Users table */}
            <div className="card">
                {isLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                            ))}
                        </div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="card-body text-center py-16">
                        <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900">No users found</h3>
                        <p className="text-slate-500 mt-1">Add your first user to get started.</p>
                        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary btn-md mt-4">
                            Add User
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Username</th>
                                    <th>Role</th>
                                    <th>Locations</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user: any) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div>
                                                <p className="font-medium">{user.fullName}</p>
                                                {user.email && (
                                                    <p className="text-xs text-slate-500">{user.email}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="font-mono text-sm">{user.username}</td>
                                        <td>
                                            <span className={`badge ${roleColors[user.role as keyof typeof roleColors]}`}>
                                                {user.role.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            {user.locationIds && user.locationIds.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {user.locationIds.map((locId: string) => {
                                                        const location = locations.find((l: any) => l.id === locId);
                                                        return location ? (
                                                            <span
                                                                key={locId}
                                                                className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700"
                                                            >
                                                                {location.name}
                                                            </span>
                                                        ) : null;
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400 text-sm">All locations</span>
                                            )}
                                        </td>
                                        <td>
                                            {user.isActive ? (
                                                <span className="badge badge-success">Active</span>
                                            ) : (
                                                <span className="badge badge-error">Inactive</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="btn btn-ghost btn-sm"
                                                    title="Edit user"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleResetPassword(user.id)}
                                                    className="btn btn-ghost btn-sm"
                                                    title="Reset password"
                                                >
                                                    <Key className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm(`Deactivate ${user.fullName}?`)) {
                                                            deleteMutation.mutate(user.id);
                                                        }
                                                    }}
                                                    className="btn btn-ghost btn-sm text-error-600"
                                                    title="Deactivate user"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit User Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingUser ? 'Edit User' : 'Add New User'}
                            </h2>
                            <button onClick={handleCloseModal} className="p-1 hover:bg-slate-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Full Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.fullName ? 'input-error' : ''}`}
                                        placeholder="John Doe"
                                        {...register('fullName')}
                                    />
                                    {errors.fullName && <p className="error-text">{errors.fullName.message}</p>}
                                </div>

                                <div>
                                    <label className="label">Username *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.username ? 'input-error' : ''}`}
                                        placeholder="johndoe"
                                        disabled={!!editingUser}
                                        {...register('username')}
                                    />
                                    {errors.username && <p className="error-text">{errors.username.message}</p>}
                                    {editingUser && (
                                        <p className="text-xs text-slate-500 mt-1">Username cannot be changed</p>
                                    )}
                                </div>

                                {!editingUser && (
                                    <div>
                                        <label className="label">Password *</label>
                                        <input
                                            type="password"
                                            className={`input ${errors.password ? 'input-error' : ''}`}
                                            placeholder="••••••••"
                                            {...register('password')}
                                        />
                                        {errors.password && <p className="error-text">{errors.password.message}</p>}
                                    </div>
                                )}

                                <div>
                                    <label className="label">Email</label>
                                    <input
                                        type="email"
                                        className={`input ${errors.email ? 'input-error' : ''}`}
                                        placeholder="john@example.com"
                                        {...register('email')}
                                    />
                                    {errors.email && <p className="error-text">{errors.email.message}</p>}
                                </div>

                                <div>
                                    <label className="label">Role *</label>
                                    <select className={`input ${errors.role ? 'input-error' : ''}`} {...register('role')}>
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Assigned Locations</label>
                                    <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3">
                                        {locations.map((location: any) => (
                                            <label key={location.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    value={location.id}
                                                    className="w-4 h-4"
                                                    {...register('locationIds')}
                                                />
                                                <span className="text-sm">{location.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Leave empty for access to all locations
                                    </p>
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button type="button" onClick={handleCloseModal} className="btn btn-secondary btn-md">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createMutation.isPending || updateMutation.isPending
                                        ? 'Saving...'
                                        : editingUser
                                            ? 'Update User'
                                            : 'Add User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {isPasswordModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-md">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Reset Password</h2>
                            <button
                                onClick={() => {
                                    setIsPasswordModalOpen(false);
                                    setResetPasswordUserId('');
                                    resetPasswordForm.reset();
                                }}
                                className="p-1 hover:bg-slate-100 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={resetPasswordForm.handleSubmit(onSubmitPasswordReset)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">New Password</label>
                                    <input
                                        type="password"
                                        className="input"
                                        placeholder="••••••••"
                                        {...resetPasswordForm.register('newPassword', {
                                            required: 'Password is required',
                                            minLength: { value: 8, message: 'Minimum 8 characters' },
                                        })}
                                    />
                                    {resetPasswordForm.formState.errors.newPassword && (
                                        <p className="error-text">
                                            {resetPasswordForm.formState.errors.newPassword.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsPasswordModalOpen(false);
                                        setResetPasswordUserId('');
                                        resetPasswordForm.reset();
                                    }}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={resetPasswordMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
